<script setup lang="ts">
import { computed } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Button from '@/components/ui/UIButton.vue';
import Badge from '@/components/ui/Badge.vue';
import Separator from '@/components/ui/Separator.vue';
import { Play, Clock, Brain, Loader2, CheckCircle2, AlertCircle } from 'lucide-vue-next';
import type { Document } from '@/types/ui/PromptManager';
import type { ModelInvocationStatus } from '@/types/ai';

interface Props {
  canExecute: boolean;
  isExecuting: boolean;
  selectedDocument: Document | null;
  selectedModel: string;
  promptContent: string;
  error?: string;
  invocationId?: string | null;
  invocationStatus?: ModelInvocationStatus | null;
}

const props = defineProps<Props>();

defineEmits<{
  execute: [];
}>();

const executionStatus = computed(() => {
  if (props.invocationStatus === 'FAILED') return 'failed';
  if (props.invocationStatus === 'COMPLETED') return 'completed';
  if (props.invocationStatus === 'RUNNING') return 'running';
  if (props.invocationStatus === 'PENDING') return 'pending';
  if (props.isExecuting) return 'executing';
  // if (!props.selectedDocument) return 'no-document';
  if (!props.selectedModel) return 'no-model';
  if (!props.promptContent) return 'no-prompt';
  return 'ready';
});

const statusMessage = computed(() => {
  switch (executionStatus.value) {
    case 'pending':
      return 'Invocation queued, waiting to start...';
    case 'running':
      return 'Processing document with AI model...';
    case 'completed':
      return 'Execution completed successfully';
    case 'failed':
      return 'Execution failed';
    case 'executing':
      return 'Processing document with AI model...';
    // case 'no-document':
    //   return 'Please select a document to process';
    case 'no-prompt':
      return 'Please select or create a prompt';
    case 'no-model':
      return 'Please select a model';
    case 'ready':
      return 'Ready to execute prompt';
    default:
      return '';
  }
});

const statusColor = computed(() => {
  switch (executionStatus.value) {
    case 'pending':
      return 'tw:text-yellow-600';
    case 'running':
    case 'executing':
      return 'tw:text-blue-600';
    case 'completed':
      return 'tw:text-green-600';
    case 'failed':
      return 'tw:text-red-600';
    // case 'no-document':
    case 'no-prompt':
      return 'tw:text-amber-600';
    case 'ready':
      return 'tw:text-green-600';
    default:
      return 'tw:text-slate-600';
  }
});
</script>

<template>
  <Card class="tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="tw:flex tw:items-center tw:gap-2">
        <Play class="tw:h-5 tw:w-5 tw:text-green-600" />
        Execution
      </CardTitle>
      <CardDescription> Run prompts against selected documents </CardDescription>
    </CardHeader>
    <CardContent class="tw:space-y-4">
      <!-- Status -->
      <div class="tw:rounded-lg tw:border tw:bg-slate-50 tw:p-4">
        <div class="tw:mb-3 tw:flex tw:items-center tw:gap-2">
          <Loader2 v-if="executionStatus === 'running' || executionStatus === 'executing'" class="tw:h-4 tw:w-4 tw:animate-spin tw:text-blue-600" />
          <Clock v-else-if="executionStatus === 'pending'" class="tw:h-4 tw:w-4 tw:animate-pulse tw:text-yellow-600" />
          <CheckCircle2 v-else-if="executionStatus === 'completed'" class="tw:h-4 tw:w-4 tw:text-green-600" />
          <AlertCircle v-else-if="executionStatus === 'failed'" class="tw:h-4 tw:w-4 tw:text-red-600" />
          <Clock v-else class="tw:h-4 tw:w-4 tw:text-slate-500" />
          <span :class="['tw:text-sm tw:font-medium', statusColor]">
            {{ statusMessage }}
          </span>
        </div>
        <div v-if="invocationId" class="tw:mb-2 tw:text-xs tw:text-slate-500">
          Invocation ID: {{ invocationId }}
        </div>

        <!-- Execution Details -->
        <div class="tw:space-y-3">
          <!--TODO: commented out until the document processing is complete -->
          <!-- <div class="tw:flex tw:items-center tw:justify-between"> -->
          <!--   <span class="tw:text-sm tw:text-slate-600">Document:</span> -->
          <!--   <Badge v-if="selectedDocument" variant="outline"> -->
          <!--     <FileText class="tw:w-3 tw:h-3 tw:mr-1" /> -->
          <!--     {{ selectedDocument.fileName }} -->
          <!--   </Badge> -->
          <!--   <Badge v-else variant="outline">Not selected</Badge> -->
          <!-- </div> -->

          <Separator />

          <div class="tw:flex tw:items-center tw:justify-between">
            <span class="tw:text-sm tw:text-slate-600">Prompt:</span>
            <Badge v-if="promptContent" variant="outline">
              <Brain class="tw:mr-1 tw:h-3 tw:w-3" />
              {{ promptContent.length }} characters
            </Badge>
            <Badge v-else variant="outline">Not selected</Badge>
          </div>

          <template v-if="executionStatus === 'pending' || executionStatus === 'running' || executionStatus === 'executing'">
            <Separator />
            <div class="tw:flex tw:items-center tw:justify-between">
              <span class="tw:text-sm tw:text-slate-600">Progress:</span>
              <div class="tw:flex tw:items-center tw:gap-2">
                <div class="tw:h-2 tw:w-24 tw:rounded-full tw:bg-slate-200">
                  <div 
                    :class="[
                      'tw:h-2 tw:rounded-full',
                      executionStatus === 'pending' ? 'tw:animate-pulse tw:bg-yellow-600' : 'tw:animate-pulse tw:bg-blue-600'
                    ]" 
                    :style="executionStatus === 'pending' ? 'width: 30%' : 'width: 60%'"></div>
                </div>
                <span class="tw:text-xs tw:text-slate-500">
                  {{ executionStatus === 'pending' ? 'Queued...' : 'Processing...' }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Execute Button -->
      <Button
        @click="$emit('execute')"
        :disabled="!canExecute"
        class="tw:w-full tw:bg-linear-to-r tw:from-green-600 tw:to-blue-600 tw:hover:from-green-700 tw:hover:to-blue-700 tw:disabled:from-slate-400 tw:disabled:to-slate-400"
        size="lg">
        <template v-if="isExecuting">
          <Loader2 class="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
          Executing Prompt...
        </template>
        <template v-else>
          <Play class="tw:mr-2 tw:h-4 tw:w-4" />
          Execute Prompt
        </template>
      </Button>

      <!-- Error Message -->
      <div v-if="error" class="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-600">
        <p class="tw:font-medium">Error:</p>
        <p class="tw:mt-1">{{ error }}</p>
      </div>

      <!-- Execution Info -->
      <div class="tw:space-y-1 tw:text-xs tw:text-slate-500">
        <p>• Execution typically takes 5-10 seconds</p>
        <p>• Results will appear in JSON format</p>
        <p>• Large documents may take longer to process</p>
      </div>
    </CardContent>
  </Card>
</template>
